import { useLoginOrCheckout } from 'webReservations/checkout/useLoginOrCheckout';
import { Button, ButtonVariants } from '@components/button/Button';
import {
  BookingUnit,
  RestaurantEvent,
  RestaurantEventTier,
} from '@shared/types/restaurants';
import { centsToWholeDollar } from '@shared/utils/currency';
import cx from 'classnames';
import typography from '~styles/typography.scss';
import {
  GUEST_COUNT_SEARCH_PARAM,
  LOW_INVENTORY_MAX_SPOTS,
  RESTAURANT_EVENT_TIER_ID,
  SELECTED_ADD_ONS,
  SELECTED_FLOOR_PLAN_TABLE_ID,
} from './constants';
import type { RestaurantEventAddOn } from '@shared/types/restaurantEvents';
import { AddOnSelectionModal } from './AddOnSelectionModal';
import type { SelectedAddOn } from './apiHelpers';
import styles from './RestaurantEventTiersList.scss';
import {
  useFeatureFlagContext,
  WebReservationsFeatureFlag,
} from 'webReservations/featureFlags';
import { useIsOpenWithData } from '@shared/hooks/useIsOpenWithData';
import { useQueries, useQuery } from '@tanstack/react-query';
import { useRestaurantContext } from 'webReservations/context/RestaurantContext';
import { getFloorPlansForRestaurantId } from 'webReservations/floorPlans/apiHelpers';
import { getAvailableTablesForTier, TierTablePricing } from './apiHelpers';
import { FloorPlanData, FloorPlanTableData } from '@shared/types/floorPlans';
import { useState } from 'react';
import { TableSelectionModal } from './TableSelectionModal';
import { getSeatsLeftLabel } from './ticketPurchase/tierAvailability';

interface RestaurantEventTiersListContainerProps {
  guestCount: number;
  tiers: RestaurantEvent['tiers'];
  addOns: RestaurantEventAddOn[];
  restaurantEventId: string;
  eventCapacity: number;
  bookingUnit: BookingUnit;
}

interface RestaurantEventTiersListProps {
  guestCount: number;
  tiers: RestaurantEvent['tiers'];
  addOns: RestaurantEventAddOn[];
  floorPlans: FloorPlanData[];
  loginOrCheckout: (checkoutPath: string) => Promise<void>;
  isEnabled: (flag: WebReservationsFeatureFlag) => boolean;
  availableTablesGroupedByTierId: {
    [k: string]: TierTablePricing[];
  };
  eventCapacity: number;
  bookingUnit: BookingUnit;
}

export const RestaurantEventTiersListContainer = ({
  guestCount,
  tiers,
  addOns,
  restaurantEventId,
  eventCapacity,
  bookingUnit,
}: RestaurantEventTiersListContainerProps) => {
  const { restaurantDetails } = useRestaurantContext();
  const { loginOrCheckout } = useLoginOrCheckout();
  const { isEnabled } = useFeatureFlagContext();

  const { data: floorPlans, isLoading: isFloorPlansQueryLoading } = useQuery({
    initialData: [],
    queryKey: ['floorPlans', restaurantDetails.id],
    queryFn: async () => {
      return await getFloorPlansForRestaurantId(restaurantDetails.id);
    },
  });

  const {
    data: availableTablesGroupedByTierId,
    isLoading: isAvailableTablesQueryLoading,
  } = useQueries({
    queries: tiers.map((tier) => ({
      queryKey: [
        'availableTablesForTier',
        restaurantEventId,
        tier.id,
        guestCount,
      ],
      queryFn: () =>
        getAvailableTablesForTier(
          restaurantDetails.id,
          restaurantEventId,
          tier.id,
          guestCount,
        ),
    })),
    combine: (results) => ({
      data: Object.fromEntries(
        results.map((result, index) => [tiers[index].id, result.data ?? []]),
      ),
      isLoading: results.some((result) => result.isLoading),
    }),
  });

  if (isFloorPlansQueryLoading || isAvailableTablesQueryLoading) {
    return null;
  }

  return (
    <RestaurantEventTiersList
      guestCount={guestCount}
      tiers={tiers}
      addOns={addOns}
      floorPlans={floorPlans}
      loginOrCheckout={loginOrCheckout}
      isEnabled={isEnabled}
      availableTablesGroupedByTierId={availableTablesGroupedByTierId}
      eventCapacity={eventCapacity}
      bookingUnit={bookingUnit}
    />
  );
};

interface AddOnModalData {
  tierId: string;
  selectedFloorPlanTableId: string | null;
}

export const RestaurantEventTiersList = ({
  guestCount,
  tiers,
  addOns,
  floorPlans,
  loginOrCheckout,
  isEnabled,
  availableTablesGroupedByTierId,
  eventCapacity,
  bookingUnit,
}: RestaurantEventTiersListProps) => {
  const {
    open: openTableModal,
    isOpen: isTableModalOpen,
    data: selectedTier,
    close: closeTableModal,
  } = useIsOpenWithData<RestaurantEventTier>();

  const {
    open: openAddOnModal,
    isOpen: isAddOnModalOpen,
    data: addOnModalData,
    close: closeAddOnModal,
  } = useIsOpenWithData<AddOnModalData>();

  const [selectedFloorPlanId, setSelectedFloorPlanId] = useState<
    string | undefined
  >(floorPlans[0]?.id);

  const [selectedFloorPlanTable, setSelectedFloorPlanTable] = useState<
    FloorPlanTableData | undefined
  >();

  const availableTablesForSelectedTier =
    availableTablesGroupedByTierId[selectedTier?.id || ''] ?? [];

  const hasAvailableAddOnsForTier = (tierId: string): boolean => {
    return addOns.some((addOn) => {
      const addOnTiers = addOn.tiers ?? [];
      return (
        addOnTiers.length === 0 || addOnTiers.some((tier) => tier.id === tierId)
      );
    });
  };

  const shouldShowAddOnModal = (tierId: string): boolean =>
    isEnabled('purchaseEventAddOns') && hasAvailableAddOnsForTier(tierId);

  const navigateToCheckout = (
    tierId: string,
    selectedFloorPlanTableId: string | null,
    selectedAddOns?: SelectedAddOn[],
  ) => {
    const params: Record<string, string> = {
      [GUEST_COUNT_SEARCH_PARAM]: String(guestCount),
      [RESTAURANT_EVENT_TIER_ID]: tierId,
    };
    if (selectedFloorPlanTableId) {
      params[SELECTED_FLOOR_PLAN_TABLE_ID] = selectedFloorPlanTableId;
    }
    if (selectedAddOns && selectedAddOns.length > 0) {
      params[SELECTED_ADD_ONS] = JSON.stringify(selectedAddOns);
    }
    const searchParams = new URLSearchParams(params);
    loginOrCheckout(`checkout?${searchParams}`);
  };

  const proceedAfterTableSelection = (
    tierId: string,
    selectedFloorPlanTableId: string | null,
  ) => {
    if (shouldShowAddOnModal(tierId)) {
      openAddOnModal({ tierId, selectedFloorPlanTableId });
    } else {
      navigateToCheckout(tierId, selectedFloorPlanTableId);
    }
  };

  const totalEventTicketsSold = tiers.reduce(
    (sum, tier) => sum + tier.ticketsSold,
    0,
  );
  const isEventSoldOut = totalEventTicketsSold >= eventCapacity;

  return (
    <>
      <ul className={styles.tiersList}>
        {tiers.map((tier) => {
          const isTierSoldOut = tier.ticketsSold >= tier.capacity;
          const isSoldOut = isEventSoldOut || isTierSoldOut;
          const spotsLeft = tier.capacity - tier.ticketsSold;
          const isTierAtCapacityOfSelectedGuestCount =
            !isSoldOut && guestCount > spotsLeft;
          const showLowInventory =
            !isSoldOut &&
            spotsLeft >= 1 &&
            spotsLeft <= LOW_INVENTORY_MAX_SPOTS;
          const seatsLeftLabel = getSeatsLeftLabel(spotsLeft, bookingUnit);

          return (
            <li
              key={tier.id}
              className={cx(styles.tierListItem, {
                [styles.tierSoldOut]: isSoldOut,
              })}
            >
              <div className={styles.ticketSection}>
                <div className={styles.tierTitleColumn}>
                  <span className={typography.c3_20}>{tier.name}</span>
                </div>
                <span className={cx(typography.h7m, styles.price)}>
                  {isSoldOut
                    ? 'Sold out'
                    : centsToWholeDollar(tier.price * guestCount)}
                </span>
              </div>
              <div className={styles.ticketSection}>
                {isTierAtCapacityOfSelectedGuestCount && (
                  <span className={styles.lowInventoryBadge} role="status">
                    Not enough tickets for your guest total.
                    <br />
                    {seatsLeftLabel}
                  </span>
                )}
                {showLowInventory && !isTierAtCapacityOfSelectedGuestCount && (
                  <span className={styles.lowInventoryBadge} role="status">
                    {`${seatsLeftLabel} Secure your spot now.`}
                  </span>
                )}
              </div>

              <div className={styles.checkoutButtonContainer}>
                <Button
                  className={styles.checkoutButton}
                  variant={
                    isSoldOut
                      ? ButtonVariants.Secondary
                      : ButtonVariants.Primary
                  }
                  label={isSoldOut ? 'Sold out' : 'Continue to Checkout'}
                  isDisabled={isSoldOut || isTierAtCapacityOfSelectedGuestCount}
                  onClick={() => {
                    const hasTableAvailability =
                      availableTablesGroupedByTierId[tier.id].length > 0;

                    const shouldOpenTableSelectionModal =
                      isEnabled('chooseTablesForTiers') && hasTableAvailability;

                    if (shouldOpenTableSelectionModal) {
                      openTableModal(tier);
                    } else {
                      proceedAfterTableSelection(tier.id, null);
                    }
                  }}
                />
              </div>
            </li>
          );
        })}
      </ul>

      <TableSelectionModal
        isOpen={isTableModalOpen}
        onClose={() => {
          closeTableModal();
          setSelectedFloorPlanTable(undefined);
        }}
        floorPlans={floorPlans}
        selectedFloorPlanId={selectedFloorPlanId}
        onSelectFloorPlanId={setSelectedFloorPlanId}
        availableTables={availableTablesForSelectedTier}
        selectedFloorPlanTable={selectedFloorPlanTable}
        bookingUnit={bookingUnit}
        guestCount={guestCount}
        onSelectTable={setSelectedFloorPlanTable}
        onConfirm={(table) => {
          closeTableModal();
          proceedAfterTableSelection(selectedTier!.id, table.id);
        }}
        onSkip={() => {
          closeTableModal();
          proceedAfterTableSelection(selectedTier!.id, null);
        }}
      />

      <AddOnSelectionModal
        isOpen={isAddOnModalOpen}
        onClose={closeAddOnModal}
        addOns={addOns}
        selectedTierId={addOnModalData?.tierId ?? ''}
        onContinue={(selectedAddOns) => {
          closeAddOnModal();
          navigateToCheckout(
            addOnModalData!.tierId,
            addOnModalData!.selectedFloorPlanTableId,
            selectedAddOns,
          );
        }}
        onSkip={() => {
          closeAddOnModal();
          navigateToCheckout(
            addOnModalData!.tierId,
            addOnModalData!.selectedFloorPlanTableId,
          );
        }}
      />
    </>
  );
};
